/**
 * Geometry of the design editor (pure, unit-tested in geo.test.ts).
 *
 * Every design coordinate is local ENU metres from the version's frame origin (x east, y north, z up; the
 * backend's transverse Mercator frame, which equals the tangent plane at the origin to well under a centimetre at
 * roof scale). Everything else is derived from it:
 * - the 3D scene is three.js Y-up: scene = (east, up, -north);
 * - Google's 3D tiles are in ECEF (WGS84); `ecefToSceneMatrix` places them in the scene through the frame origin;
 * - the 2D view draws the pipeline's imagery with the `enu_to_px` affine the API derives.
 */
export type Vec2 = [number, number];
export type Vec3 = [number, number, number];

const A = 6378137.0; // WGS84 semi-major axis
const F = 1 / 298.257223563;
const E2 = F * (2 - F);
const RAD = Math.PI / 180;

export interface GeoOrigin {
  lat: number;
  lng: number;
  /** Height above the WGS84 ellipsoid, metres. */
  h: number;
}

export function geodeticToEcef(lat: number, lng: number, h: number): Vec3 {
  const phi = lat * RAD;
  const lam = lng * RAD;
  const n = A / Math.sqrt(1 - E2 * Math.sin(phi) ** 2);
  return [
    (n + h) * Math.cos(phi) * Math.cos(lam),
    (n + h) * Math.cos(phi) * Math.sin(lam),
    (n * (1 - E2) + h) * Math.sin(phi),
  ];
}

export function ecefToGeodetic([x, y, z]: Vec3): GeoOrigin {
  const lng = Math.atan2(y, x);
  const p = Math.hypot(x, y);
  let phi = Math.atan2(z, p * (1 - E2));
  let h = 0;
  for (let i = 0; i < 6; i++) {
    const n = A / Math.sqrt(1 - E2 * Math.sin(phi) ** 2);
    h = p / Math.cos(phi) - n;
    phi = Math.atan2(z, p * (1 - (E2 * n) / (n + h)));
  }
  return { lat: phi / RAD, lng: lng / RAD, h };
}

/** Unit east, north and up vectors (ECEF) at a geodetic position. */
export function enuBasis(lat: number, lng: number): { east: Vec3; north: Vec3; up: Vec3 } {
  const phi = lat * RAD;
  const lam = lng * RAD;
  return {
    east: [-Math.sin(lam), Math.cos(lam), 0],
    north: [-Math.sin(phi) * Math.cos(lam), -Math.sin(phi) * Math.sin(lam), Math.cos(phi)],
    up: [Math.cos(phi) * Math.cos(lam), Math.cos(phi) * Math.sin(lam), Math.sin(phi)],
  };
}

export function enuToEcef(origin: GeoOrigin, [e, n, u]: Vec3): Vec3 {
  const o = geodeticToEcef(origin.lat, origin.lng, origin.h);
  const { east, north, up } = enuBasis(origin.lat, origin.lng);
  return [0, 1, 2].map((i) => o[i]! + e * east[i]! + n * north[i]! + u * up[i]!) as Vec3;
}

export function ecefToEnu(origin: GeoOrigin, p: Vec3): Vec3 {
  const o = geodeticToEcef(origin.lat, origin.lng, origin.h);
  const d: Vec3 = [p[0] - o[0], p[1] - o[1], p[2] - o[2]];
  const { east, north, up } = enuBasis(origin.lat, origin.lng);
  return [dot(d, east), dot(d, north), dot(d, up)];
}

/** ENU metres → three.js scene (Y up, -Z north). */
export function enuToScene([e, n, u]: Vec3): Vec3 {
  return [e, u, -n];
}

export function sceneToEnu([x, y, z]: Vec3): Vec3 {
  return [x, -z, y];
}

/**
 * Row-major 4×4 matrix taking ECEF metres to scene coordinates: translate to the origin, rotate into ENU, swap to
 * the scene's axes. Feed it to `Matrix4.set(...)`.
 */
export function ecefToSceneMatrix(origin: GeoOrigin): number[] {
  const o = geodeticToEcef(origin.lat, origin.lng, origin.h);
  const { east, north, up } = enuBasis(origin.lat, origin.lng);
  // scene x = east·d, scene y = up·d, scene z = -north·d, with d = p - o
  const rows: Vec3[] = [east, up, [-north[0], -north[1], -north[2]]];
  const m: number[] = [];
  for (const r of rows) m.push(r[0], r[1], r[2], -dot(r, o));
  m.push(0, 0, 0, 1);
  return m;
}

/**
 * The frame origin on the WGS84 ellipsoid. Our z = 0 is `elevation_m` above the EGM96 geoid (the Solar API DSM's
 * datum), Google's 3D tiles are ellipsoidal (ECEF): h = H + N, with N the geoid undulation at the origin (EGM96,
 * about -17 m in Calgary, +17 m at 0°/0°). Without it the tiles sit N metres off our roof.
 */
export function ellipsoidalOrigin(origin: { lat: number; lng: number; elevation_m: number }, undulationM: number): GeoOrigin {
  return { lat: origin.lat, lng: origin.lng, h: origin.elevation_m + undulationM };
}

/**
 * A camera position (ENU) looking at `target` from the compass direction `azimuthDeg` (0 = from the north,
 * clockwise), `elevationDeg` above the horizon, `distance` metres away.
 */
export function orbitPosition(target: Vec3, azimuthDeg: number, elevationDeg: number, distance: number): Vec3 {
  const a = azimuthDeg * RAD;
  const e = elevationDeg * RAD;
  return [
    target[0] + distance * Math.cos(e) * Math.sin(a),
    target[1] + distance * Math.cos(e) * Math.cos(a),
    target[2] + distance * Math.sin(e),
  ];
}

/** Circular mean of compass directions (degrees, weighted); null when they cancel out. */
export function meanAzimuth(items: { azimuth: number; weight: number }[]): number | null {
  let x = 0;
  let y = 0;
  for (const { azimuth, weight } of items) {
    x += weight * Math.sin(azimuth * RAD);
    y += weight * Math.cos(azimuth * RAD);
  }
  if (Math.hypot(x, y) < 1e-6) return null;
  return ((Math.atan2(x, y) / RAD) % 360 + 360) % 360;
}

/** Applies a row-major 4×4 matrix to a point. */
export function applyMatrix(m: number[], [x, y, z]: Vec3): Vec3 {
  return [
    m[0]! * x + m[1]! * y + m[2]! * z + m[3]!,
    m[4]! * x + m[5]! * y + m[6]! * z + m[7]!,
    m[8]! * x + m[9]! * y + m[10]! * z + m[11]!,
  ];
}

// --------------------------------------------------------------------------------------------- 2D image
/** px = a·x + b·y + c, py = d·x + e·y + f (the API's `enu_to_px`). */
export function enuToPx(t: number[], [x, y]: Vec2): Vec2 {
  return [t[0]! * x + t[1]! * y + t[2]!, t[3]! * x + t[4]! * y + t[5]!];
}

/** The inverse affine: image pixels → ENU metres, as [A, B, C, D, E, F] with x = A·px + B·py + C, y = D·px + E·py + F. */
export function pxToEnuTransform(t: number[]): number[] {
  const [a, b, c, d, e, f] = t as [number, number, number, number, number, number];
  const det = a * e - b * d;
  if (Math.abs(det) < 1e-12) throw new Error("degenerate image transform");
  const ia = e / det;
  const ib = -b / det;
  const id = -d / det;
  const ie = a / det;
  return [ia, ib, -(ia * c + ib * f), id, ie, -(id * c + ie * f)];
}

// --------------------------------------------------------------------------------------------- planes
/** Unit normal (z up) of a face sloping down towards `azimuthDeg` (0 = north, clockwise), as the backend. */
export function normalFrom(pitchDeg: number, azimuthDeg: number): Vec3 {
  const p = pitchDeg * RAD;
  const a = azimuthDeg * RAD;
  return [Math.sin(p) * Math.sin(a), Math.sin(p) * Math.cos(a), Math.cos(p)];
}

export function pitchAzimuth([nx, ny, nz]: Vec3): { pitch: number; azimuth: number } {
  const pitch = Math.acos(Math.max(-1, Math.min(1, nz))) / RAD;
  const azimuth = Math.hypot(nx, ny) < 1e-9 ? 0 : ((Math.atan2(nx, ny) / RAD) % 360 + 360) % 360;
  return { pitch, azimuth };
}

/** Height of the plane normal·p + d = 0 at (x, y). */
export function zOnPlane(normal: Vec3, d: number, x: number, y: number): number {
  return -(normal[0] * x + normal[1] * y + d) / normal[2];
}

/** `u` horizontal along the eave, `v` up the slope (the backend's `uv_axes`). */
export function uvAxes(normal: Vec3): { u: Vec3; v: Vec3 } {
  const { pitch, azimuth } = pitchAzimuth(normal);
  if (pitch < 0.5) return { u: [1, 0, 0], v: [0, 1, 0] };
  const az = azimuth * RAD;
  const p = pitch * RAD;
  return {
    u: [-Math.cos(az), Math.sin(az), 0],
    v: [-Math.sin(az) * Math.cos(p), -Math.cos(az) * Math.cos(p), Math.sin(p)],
  };
}

export interface UvFrame {
  origin: Vec3;
  u: Vec3;
  v: Vec3;
}

export function uvToXyz(f: UvFrame, [s, t]: Vec2): Vec3 {
  return [0, 1, 2].map((i) => f.origin[i]! + s * f.u[i]! + t * f.v[i]!) as Vec3;
}

/** A plan point lifted onto the plane, in the face's uv frame. */
export function planToUv(f: UvFrame, normal: Vec3, d: number, [x, y]: Vec2): Vec2 {
  const p: Vec3 = [x - f.origin[0], y - f.origin[1], zOnPlane(normal, d, x, y) - f.origin[2]];
  return [dot(p, f.u), dot(p, f.v)];
}

export function dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

export function dist3(a: Vec3, b: Vec3): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}
